import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RainType, RainValidationRequestType } from "sagas/types/rainModal";
import { RootState } from "store";
import { OPPOSE_RAIN_TYPES } from "utils/constants";
import {
  RainCreationParamsType,
  RainDropDownState,
  RainModalStateType,
  RainsFilters,
  RainTimeFilter,
  RainUpdateParamsType,
  ToggleOrderType,
} from "./types/rainModal";

export const initialState: RainModalStateType = {
  loading: false,
  dropdownState: "closed",
  order: {
    next: "ASC",
    previous: "DESC",
  },
  filter: {
    date: undefined,
    startAt: undefined,
    finishAt: undefined,
  },
  rainDirty: false,
  dirtyCreation: false,
  rains: [],
  validatingRainTime: false,
  timeErrorCreation: {
    initial: false,
    end: false,
  },
  timeErrorUpdate: {
    initial: false,
    end: false,
  },
  blockedTab: {
    next: false,
    previous: false,
  },
};

const SLICE_NAME = "rainModal";

export const rainModalSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setDropdownState: (
      state: RainModalStateType,
      action: PayloadAction<RainDropDownState>
    ) => {
      state.dropdownState = action.payload;
    },
    setRainDirty: (
      state: RainModalStateType,
      action: PayloadAction<boolean>
    ) => {
      state.rainDirty = action.payload;
    },
    setDirtyCreation: (
      state: RainModalStateType,
      action: PayloadAction<boolean>
    ) => {
      state.dirtyCreation = action.payload;
    },
    setFilter: (
      state: RainModalStateType,
      action: PayloadAction<RainTimeFilter>
    ) => {
      state.filter = action.payload;
    },
    setOrder: (
      state: RainModalStateType,
      action: PayloadAction<ToggleOrderType>
    ) => {
      state.order[action.payload.tab] = action.payload.newOrder;
    },
    getRainsFetch: (
      state: RainModalStateType,
      action: PayloadAction<RainsFilters>
    ) => {
      state.loading = true;
    },
    getRainsError: (state: RainModalStateType) => {
      state.loading = false;
    },
    getRainsSuccess: (
      state: RainModalStateType,
      action: PayloadAction<RainType[]>
    ) => {
      state.loading = false;
      state.rains = action.payload;
    },
    addRainFetch: (
      state: RainModalStateType,
      action: PayloadAction<RainCreationParamsType>
    ) => {
      state.loading = true;
    },
    addRainError: () => {},
    addRainSuccess: () => {},
    deleteRainFetch: (
      state: RainModalStateType,
      action: PayloadAction<string>
    ) => {
      state.loading = true;
    },
    deleteRainError: () => {},
    deleteRainSuccess: () => {},
    updateRainFetch: (
      state: RainModalStateType,
      action: PayloadAction<RainUpdateParamsType>
    ) => {
      state.loading = true;
    },
    updateRainError: () => {},
    updateRainSuccess: () => {},
    validateRainTimeCreationFetch: (
      state: RainModalStateType,
      action: PayloadAction<RainValidationRequestType>
    ) => {
      state.validatingRainTime = true;
    },
    validateRainTimeCreationError: (
      state: RainModalStateType,
      action: PayloadAction<string[]>
    ) => {
      const errors = action.payload;
      if (errors.includes("startAt")) state.timeErrorCreation.initial = true;
      else state.timeErrorCreation.initial = false;
      if (errors.includes("finishAt")) state.timeErrorCreation.end = true;
      else state.timeErrorCreation.end = false;
      state.validatingRainTime = false;
    },
    validateRainTimeCreationSuccess: (state: RainModalStateType) => {
      state.validatingRainTime = false;
      state.timeErrorCreation.end = false;
      state.timeErrorCreation.initial = false;
    },
    validateRainTimeUpdateFetch: (
      state: RainModalStateType,
      action: PayloadAction<RainValidationRequestType>
    ) => {
      state.validatingRainTime = true;
    },
    validateRainTimeUpdateError: (
      state: RainModalStateType,
      action: PayloadAction<string[]>
    ) => {
      const errors = action.payload;
      if (errors.includes("startAt")) state.timeErrorUpdate.initial = true;
      else state.timeErrorUpdate.initial = false;
      if (errors.includes("finishAt")) state.timeErrorUpdate.end = true;
      else state.timeErrorUpdate.end = false;
      state.validatingRainTime = false;
    },
    validateRainTimeUpdateSuccess: (state: RainModalStateType) => {
      state.validatingRainTime = false;
      state.timeErrorUpdate.end = false;
      state.timeErrorUpdate.initial = false;
    },
    resetTimeErrorUpdate: (state: RainModalStateType) => {
      state.validatingRainTime = false;
      state.timeErrorUpdate.end = false;
      state.timeErrorUpdate.initial = false;
    },
    resetTimeErrorCreation: (state: RainModalStateType) => {
      state.validatingRainTime = false;
      state.timeErrorCreation.end = false;
      state.timeErrorCreation.initial = false;
    },
    resetBlockedTab: (state: RainModalStateType) => {
      state.blockedTab = initialState.blockedTab;
    },
    setBlockedTab: (
      state: RainModalStateType,
      action: PayloadAction<RainDropDownState>
    ) => {
      state.blockedTab[action.payload] = true;
      state.blockedTab[OPPOSE_RAIN_TYPES[action.payload]] = false;
    },
  },
});

export default rainModalSlice.reducer;

export const rainModalSelector = (state: RootState) => state.rainModal;

export const actions = rainModalSlice.actions;
